import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { contactInfo, services } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({
        title: "Missing information",
        description: "Please fill in your name, email and message.",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/quotes`, form, {
        headers: { "Content-Type": "application/json" },
        timeout: 20000,
      });
      if (res.data?.success) {
        toast({
          title: "Quote request sent!",
          description:
            res.data.message ||
            "Kyle will get back to you within 24 hours.",
        });
        setForm({ name: "", email: "", phone: "", service: "", message: "" });
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      toast({
        title: "Could not send request",
        description: detail,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-14">
          <div className="text-brand-yellow text-sm font-bold tracking-[0.3em] mb-3">
            GET IN TOUCH
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-brand-dark leading-tight">
            READY TO START YOUR PROJECT?
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto mt-6">
            Get in touch for a free, no-obligation quote. We're happy to discuss your project and offer honest advice.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Info */}
          <div className="lg:col-span-2 space-y-4">
            <a
              href={`tel:${contactInfo.phone.replace(/\s/g, "")}`}
              className="flex items-start gap-4 p-6 bg-brand-dark rounded-lg hover:bg-brand-dark-2 transition-colors group"
            >
              <div className="w-12 h-12 bg-brand-yellow rounded-md flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                <Phone size={20} className="text-brand-dark" />
              </div>
              <div>
                <div className="text-brand-yellow text-xs font-bold tracking-[0.2em] mb-1">
                  CALL US
                </div>
                <div className="text-white font-display text-lg tracking-wide">
                  {contactInfo.phone}
                </div>
              </div>
            </a>

            <a
              href={`mailto:${contactInfo.email}`}
              className="flex items-start gap-4 p-6 bg-brand-dark rounded-lg hover:bg-brand-dark-2 transition-colors group"
            >
              <div className="w-12 h-12 bg-brand-yellow rounded-md flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                <Mail size={20} className="text-brand-dark" />
              </div>
              <div>
                <div className="text-brand-yellow text-xs font-bold tracking-[0.2em] mb-1">
                  EMAIL US
                </div>
                <div className="text-white text-base break-all">
                  {contactInfo.email}
                </div>
              </div>
            </a>

            <div className="flex items-start gap-4 p-6 bg-brand-dark rounded-lg">
              <div className="w-12 h-12 bg-brand-yellow rounded-md flex items-center justify-center shrink-0">
                <MapPin size={20} className="text-brand-dark" />
              </div>
              <div>
                <div className="text-brand-yellow text-xs font-bold tracking-[0.2em] mb-1">
                  SERVICE AREA
                </div>
                <div className="text-white text-base">
                  Derbyshire, Nottinghamshire &amp; Leicestershire
                </div>
              </div>
            </div>

            <div className="flex items-start gap-4 p-6 bg-brand-dark rounded-lg">
              <div className="w-12 h-12 bg-brand-yellow rounded-md flex items-center justify-center shrink-0">
                <Clock size={20} className="text-brand-dark" />
              </div>
              <div>
                <div className="text-brand-yellow text-xs font-bold tracking-[0.2em] mb-1">
                  WORKING HOURS
                </div>
                <div className="text-white text-base">{contactInfo.hours}</div>
              </div>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="lg:col-span-3 bg-gray-50 border border-gray-100 rounded-lg p-8 md:p-10 shadow-sm"
          >
            <h3 className="font-display text-2xl text-brand-dark tracking-wide mb-6">
              REQUEST A FREE QUOTE
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs font-bold text-brand-dark tracking-wider mb-2 block">
                  YOUR NAME *
                </label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="John Doe"
                  className="bg-white border-gray-200 h-12"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-brand-dark tracking-wider mb-2 block">
                  EMAIL *
                </label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="john@example.com"
                  className="bg-white border-gray-200 h-12"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs font-bold text-brand-dark tracking-wider mb-2 block">
                  PHONE
                </label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="07700 900000"
                  className="bg-white border-gray-200 h-12"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-brand-dark tracking-wider mb-2 block">
                  SERVICE
                </label>
                <Select
                  value={form.service}
                  onValueChange={(v) => setForm({ ...form, service: v })}
                >
                  <SelectTrigger className="bg-white border-gray-200 h-12">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((s) => (
                      <SelectItem key={s.id} value={s.title}>
                        {s.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mb-6">
              <label className="text-xs font-bold text-brand-dark tracking-wider mb-2 block">
                YOUR MESSAGE *
              </label>
              <Textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Tell us about your project..."
                rows={5}
                className="bg-white border-gray-200"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-brand-yellow hover:bg-brand-yellow-dark text-brand-dark font-bold tracking-wider text-sm px-8 py-7 rounded-md w-full md:w-auto disabled:opacity-70"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  SENDING...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-2" />
                  SEND MESSAGE
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
